<template>
  <section class="read-page">
    <div v-if="isInitialLoading" class="read-page__empty read-page__loading">
      <span class="read-page__spinner" aria-hidden="true"></span>
      Загрузка объектов...
    </div>

    <div v-else-if="!buildings.length" class="read-page__empty">
      Объекты не найдены. Для локальной разработки выполните
      <code>make db-seed</code> (нужен запущенный <code>make db-up</code>).
    </div>

    <template v-else>
      <gallery-edge-nav
        :enabled="showEdgeNav"
        @previous="goToPrevious"
        @next="goToNext"
      />

      <header class="read-page__toolbar">
        <div class="read-page__toolbar-main">
          <button type="button" class="read-page__nav-button" @click="goToPrevious">
            Назад
          </button>

          <select
            class="read-page__select"
            :value="selectedBuildingId"
            @change="onSelectChange"
          >
            <option v-for="building in buildings" :key="building.id" :value="building.id">
              {{ building.title }}{{ isDraft(building) ? ' — Draft' : '' }}
            </option>
          </select>

          <button type="button" class="read-page__nav-button" @click="goToNext">
            Вперёд
          </button>
        </div>

        <router-link class="read-page__map-link" :to="mapLink">
          Открыть на карте
        </router-link>
      </header>

      <div v-if="currentBuilding" class="read-page__content">
        <aside class="read-page__media">
          <a
            v-for="image in images"
            :key="image.id"
            class="read-page__image-link"
            :href="fullImagePath(image)"
            :data-lightbox="`building-${currentBuilding.id}`"
            :data-title="currentBuilding.title"
          >
            <img
              class="read-page__image"
              :src="previewImagePath(image)"
              :alt="currentBuilding.title"
            />
          </a>

          <button
            type="button"
            class="read-page__problem-pill"
            :style="problemPillStyle"
            @click="openProblemOnMap"
          >
            {{ problemTitle }}
          </button>
        </aside>

        <article class="read-page__details">
          <h1 class="read-page__title">
            <span>{{ currentBuilding.title }}</span>
            <span v-if="isCurrentBuildingDraft" class="read-page__draft-badge">Draft</span>
          </h1>

          <button type="button" class="read-page__address" @click="openOnMap">
            {{ currentBuilding.address }}
          </button>

          <p v-if="currentBuilding.status" class="read-page__status">
            {{ currentBuilding.status }}
          </p>

          <div class="read-page__description">
            <p
              v-for="(block, index) in descriptionBlocks"
              :key="index"
              class="read-page__description-block"
            >
              {{ block }}
            </p>
          </div>

          <p class="read-page__updated">Обновлено: {{ currentBuilding.updated_at }}</p>
        </article>
      </div>

      <div v-else class="read-page__empty">
        Объект не найден.
      </div>
    </template>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import lightbox from 'lightbox2';
import 'lightbox2/dist/css/lightbox.min.css';
import GalleryEdgeNav from '@/components/GalleryEdgeNav.vue';
import { imageOriginalPath, imageSmPath } from '@/utils/imagePath';

export default {
  name: 'ReadPage',
  components: {
    GalleryEdgeNav,
  },
  mounted() {
    lightbox.option({
      resizeDuration: 200,
      wrapAround: true,
    });
  },
  computed: {
    ...mapGetters(['buildings', 'currentBuilding', 'problems']),
    isInitialLoading() {
      // Empty list after bootstrap means no data, not a spinner (see template v-else-if).
      if (!this.buildings.length) {
        return false;
      }

      // Only while redirecting /read -> /read/:id (see ensureSelection).
      // A missing id (e.g. /read/0) must show "Объект не найден", not spin forever.
      return !this.$route.params.id;
    },
    selectedBuildingId() {
      return this.currentBuilding ? String(this.currentBuilding.id) : '';
    },
    selectedIndex() {
      if (!this.currentBuilding) {
        return -1;
      }

      return this.buildings.findIndex(
        (building) => Number(building.id) === Number(this.currentBuilding.id)
      );
    },
    images() {
      return this.currentBuilding?.images || [];
    },
    problem() {
      if (!this.currentBuilding) {
        return null;
      }

      return (
        this.problems.find(
          (problem) => Number(problem.id) === Number(this.currentBuilding.problemId)
        ) || null
      );
    },
    problemTitle() {
      if (!this.problem) {
        return 'Тип угрозы неизвестен';
      }

      if (Number(this.problem.id) === 5) {
        return 'В черной книге';
      }

      if (Number(this.problem.id) === 6) {
        return 'Сохранено';
      }

      return `Тип угрозы: ${this.problem.title}`;
    },
    problemPillStyle() {
      return {
        backgroundColor: this.problem?.color || '#333333',
      };
    },
    descriptionBlocks() {
      if (!this.currentBuilding?.description) {
        return [];
      }

      return this.currentBuilding.description
        .split('\n')
        .map((block) => block.trim())
        .filter(Boolean);
    },
    mapLink() {
      if (!this.currentBuilding) {
        return { name: 'map' };
      }

      return {
        name: 'map',
        params: { id: this.currentBuilding.id },
        query: { zoom: 1 },
      };
    },
    showEdgeNav() {
      return Boolean(this.currentBuilding && this.buildings.length > 1);
    },
    isCurrentBuildingDraft() {
      return this.isDraft(this.currentBuilding);
    },
  },
  watch: {
    buildings: {
      immediate: true,
      handler() {
        this.ensureSelection();
      },
    },
    '$route.params.id'() {
      this.ensureSelection();
    },
  },
  methods: {
    isDraft(building) {
      return building != null && Number(building.hidden) !== 0;
    },
    ensureSelection() {
      if (!this.buildings.length || this.$route.params.id) {
        return;
      }

      this.$router.replace({
        name: 'read',
        params: { id: String(this.buildings[0].id) },
      });
    },
    goToRelative(offset) {
      if (!this.buildings.length) {
        return;
      }

      const currentIndex = this.selectedIndex >= 0 ? this.selectedIndex : 0;
      const nextIndex =
        (currentIndex + offset + this.buildings.length) % this.buildings.length;
      const nextBuilding = this.buildings[nextIndex];

      this.$router.push({
        name: 'read',
        params: { id: String(nextBuilding.id) },
      });
    },
    goToPrevious() {
      this.goToRelative(-1);
    },
    goToNext() {
      this.goToRelative(1);
    },
    onSelectChange(event) {
      this.$router.push({
        name: 'read',
        params: { id: String(event.target.value) },
      });
    },
    openOnMap() {
      this.$router.push(this.mapLink);
    },
    openProblemOnMap() {
      if (!this.currentBuilding) {
        return;
      }

      this.$router.push({
        name: 'map',
        params: { id: String(this.currentBuilding.id) },
        query: {
          zoom: 1,
          problemid: String(this.currentBuilding.problemId),
        },
      });
    },
    fullImagePath(image) {
      return imageOriginalPath(image, this.currentBuilding?.id);
    },
    previewImagePath(image) {
      return imageSmPath(image, this.currentBuilding?.id);
    },
  },
};
</script>

<style scoped>
.read-page {
  min-height: 100%;
  padding: 24px;
  background: #ffffff;
  color: #2f3540;
}

.read-page__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.read-page__toolbar-main {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.read-page__nav-button,
.read-page__map-link,
.read-page__address,
.read-page__problem-pill {
  font: inherit;
}

.read-page__nav-button,
.read-page__map-link {
  border: 1px solid #d2232a;
  background: #ffffff;
  color: #d2232a;
  border-radius: 999px;
  padding: 8px 14px;
  text-decoration: none;
  cursor: pointer;
}

.read-page__nav-button:hover,
.read-page__map-link:hover {
  background: #fff3f3;
  text-decoration: none;
}

.read-page__select {
  flex: 1;
  min-width: 220px;
  padding: 10px 12px;
  border: 1px solid #d7dce2;
  border-radius: 10px;
  background: #ffffff;
}

.read-page__content {
  display: grid;
  grid-template-columns: minmax(240px, 320px) minmax(0, 1fr);
  gap: 32px;
  align-items: start;
}

.read-page__media {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.read-page__image-link {
  display: block;
  text-decoration: none;
  outline: none;
  box-shadow: none;
}

.read-page__image-link:hover,
.read-page__image-link:active,
.read-page__image-link:visited,
.read-page__image-link:focus {
  text-decoration: none;
  outline: none;
  box-shadow: none;
}

.read-page__image {
  display: block;
  width: 100%;
  border: 1px solid #d7dce2;
  border-radius: 12px;
  outline: none;
  box-shadow: none;
}

.read-page__problem-pill {
  border: 0;
  border-radius: 12px;
  color: #ffffff;
  padding: 12px 16px;
  text-align: left;
  cursor: pointer;
}

.read-page__details {
  min-width: 0;
}

.read-page__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 0 0 12px;
  color: #d2232a;
  font-size: 44px;
  line-height: 1.05;
}

.read-page__draft-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  background: #fffbeb;
  color: #b45309;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  vertical-align: middle;
}

.read-page__address {
  border: 0;
  background: transparent;
  padding: 0;
  color: #d2232a;
  font-size: 22px;
  line-height: 1.3;
  cursor: pointer;
}

.read-page__address:hover {
  text-decoration: underline;
}

.read-page__status {
  margin: 16px 0 0;
  color: #6b7280;
  font-size: 16px;
  white-space: pre-line;
}

.read-page__description {
  margin-top: 24px;
  font-size: 15px;
  line-height: 1.65;
}

.read-page__description-block {
  margin: 0 0 16px;
}

.read-page__updated {
  margin-top: 24px;
  color: #9ca3af;
}

.read-page__empty {
  padding: 48px 0;
  color: #6b7280;
}

.read-page__loading {
  display: flex;
  align-items: center;
  gap: 12px;
}

.read-page__spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #d1d5db;
  border-top-color: #d2232a;
  border-radius: 50%;
  animation: read-page-spin 0.8s linear infinite;
}

@keyframes read-page-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 960px) {
  .read-page__content {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .read-page {
    padding: 16px;
  }

  .read-page__toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .read-page__toolbar-main {
    flex-direction: column;
    align-items: stretch;
  }

  .read-page__title {
    font-size: 32px;
  }
}
</style>
